import { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEntityFinderContext } from "../context/EntityFinderContext";
import { User } from "@/bryntum-lib/models/User";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { BoardUser, UserAvatar } from "@/modern/components/user-avatar";
import { getFontSize } from "@/react/context/bryntumContext";

export const UserTab = () => {
  const { data, handleClose } = useEntityFinderContext();
  const { editorHandler } = useSwivuBryntumContext();
  const users = data as User[];
  const containerRef = useRef<HTMLDivElement>(null);

  const gap = 12;
  const rowVirtualizer = useVirtualizer({
    count: users.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => getFontSize() * 4 + gap,
    overscan: 8
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleUserClick = (user: User) => {
    // @ts-expect-error openEditor is not in the type definition
    editorHandler.openEditor(EDITOR_KEYS.userEditor, user, { user });
    handleClose();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div ref={containerRef} className="flex-1 overflow-auto bg-background">
        {users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <svg className="w-12 h-12 mb-4 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="text-sm">No users found</p>
          </div>
        ) : (
          <div className="relative w-full" style={{ height: `${totalSize}px` }}>
            {virtualItems.map((virtualRow) => {
              const user = users[virtualRow.index];
              return (
                <div
                  key={user.id}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <div
                    className="px-4 flex items-center bg-card hover:opacity-90 cursor-pointer transition-all rounded-md"
                    style={{ height: `${virtualRow.size - gap}px`, marginBottom: `${gap}px` }}
                    onClick={() => handleUserClick(user)}
                  >
                    {user.name && <UserAvatar user={user as unknown as BoardUser} size="default" showName={true} showTooltip={false} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
